import { useQuery } from '@apollo/client'
import {
  Button,
  Hidden,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import { Pagination } from '@material-ui/lab'
import { matchSorter } from 'match-sorter'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import { GATEWAYS_LIST_QUERY } from '../../graphql/gateways'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import gatewayPagesRoleManagement from '../../staticRoleManagement/gatewayRoleManagement'
import { Gateway } from '../../types/gateway'
import GatewaysFilters from './GatewayFilters'
import GatewaysTable from './GatewaysTable'
import GatewaysTableMobile from './GatewaysTableMobile'
import useStyles from './styles/index.styles'

const GatewayList = () => {
  const theme = useTheme()
  const classes = useStyles()
  const [perPage] = useState(5)
  const [page, setPage] = useState(1)
  const [count, setcount] = useState<number>(0)
  const [searchInputValue, setsearchInputValue] = useState<string>('')
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { loading, data, refetch } = useQuery<{
    gateways: Gateway[]
  }>(GATEWAYS_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [filteredGateways, setfilteredGateways] = useState<Gateway[]>([])

  useEffect(() => {
    setPage(1)
    const fullGatewaysList = data?.gateways ? data?.gateways : []

    const newFilteredGateways = matchSorter(
      fullGatewaysList,
      searchInputValue,
      {
        keys: [
          'basicDetails.name',
          'credential.gatewaysType',
          'basicDetails.entity',
          'basicDetails.displayEntityCode'
        ]
      }
    )

    setfilteredGateways(newFilteredGateways)
    setcount(newFilteredGateways.length)
  }, [searchInputValue, loading])

  const numberOfPages = useMemo(() => Math.ceil(count / perPage), [count])

  const hasAccessToAddGateway = gatewayPagesRoleManagement
    .find((item) => item.route === '/gateway')
    ?.rolesListWithWriteAccess?.includes(getRoleFromStorage())

  const hasAccessToViewGateway = gatewayPagesRoleManagement
    .find((item) => item.route === '/gateway/:id/details')
    ?.rolesListWithWriteAccess?.includes(getRoleFromStorage())

  return (
    <div>
      <PageHeader title={getTranslate('Gateways')} />

      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {isSmallScreen
            ? getTranslate('Gateways')
            : getTranslate('Gateways List')}
        </Typography>
        {hasAccessToAddGateway && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
            component={LocalizedLink}
            to="/gateway/create">
            {isSmallScreen ? getTranslate('Add') : getTranslate('Add Gateway')}
          </Button>
        )}
      </div>
      <GatewaysFilters
        inputValue={searchInputValue}
        setinputValue={setsearchInputValue}
      />
      <Hidden smDown>
        <GatewaysTable
          hasAccessToViewGateway={hasAccessToViewGateway}
          data={filteredGateways.slice((page - 1) * perPage, page * perPage)}
          loading={loading}
          reloadData={refetch}
        />
      </Hidden>
      <Hidden mdUp>
        <GatewaysTableMobile
          hasAccessToViewGateway={hasAccessToViewGateway}
          data={filteredGateways.slice((page - 1) * perPage, page * perPage)}
          loading={loading}
        />
      </Hidden>
      <div className={classes.paginationContainer}>
        <Pagination
          count={numberOfPages}
          color="primary"
          size={isSmallScreen ? 'small' : 'medium'}
          page={page}
          onChange={(_, value) => setPage(value)}
        />
      </div>
    </div>
  )
}
export default GatewayList
